import { ref, watch } from 'vue';
import type { Ref } from 'vue';
import type { AxiosInstance } from 'axios';

// The signed-in person's own face for the sidebar's account row (issue 796).
//
// The account row used to show a photo only from the demo stage; a person who has
// a People photo saw initials in her own row while the org chart, the team and
// the approval rows showed her face. The photo lives on her People record
// (`Person.photoUrl`, a root-relative `/api/people/media/<fileId>`), which every
// module UI can read the same way: `GET /persons/me` on people-api — gated on
// `person.edit_own`, which every People role carries. One read here instead of
// five bespoke sources (a module's own avatar column, a viewer store, nothing at
// all in the ATS).
//
// An account row must never error or toast: every failure — no People role (403),
// no linked Person (404), an unreachable module, a record without a photo —
// resolves to "no photo", and the row keeps its initials. The caller's client is
// handed in (a plain tenant-headers client with no error surface), like the other
// app-kit composables that reach an API.
//
// Read once per `getKey()` value (tenant + user); the key changing — a tenant
// switch, another account — clears the photo and reads again. `refresh()` re-reads
// for the one place that changes the photo from inside the app.
export type OwnPhotoDeps = {
  peopleApi: Pick<AxiosInstance, 'get'>;
  // `tenantSlug:userId` or whatever identifies the viewer; null/empty = not signed
  // in yet, nothing is read.
  getKey: () => string | null | undefined;
};

export function useOwnPhotoUrl(deps: OwnPhotoDeps): { ownPhotoUrl: Ref<string | undefined>; refresh: () => Promise<void> } {
  const ownPhotoUrl = ref<string | undefined>(undefined);
  // Guards against an answer for a key we have already moved away from.
  let generation = 0;

  async function load(): Promise<void> {
    const key = deps.getKey();
    const mine = ++generation;
    if (!key) {
      ownPhotoUrl.value = undefined;
      return;
    }
    let url: string | undefined;
    try {
      const res = await deps.peopleApi.get('/persons/me', { suppressGlobalError: true } as any);
      const photo = res?.data?.photoUrl;
      url = typeof photo === 'string' && photo.trim() ? photo.trim() : undefined;
    } catch {
      // swallowed: the account row degrades to initials, it never surfaces an error
      url = undefined;
    }
    if (mine === generation) ownPhotoUrl.value = url;
  }

  watch(() => deps.getKey(), () => { void load(); }, { immediate: true });

  return { ownPhotoUrl, refresh: load };
}
